import update from 'immutability-helper';
import { CSSProperties, FC, useEffect } from 'react';
import { useCallback, useState } from 'react';
import type { XYCoord } from 'react-dnd';
import { useDrop } from 'react-dnd';
import { Box } from './Box';
import type { DragItem, GameContainerProps, GameDragObject } from './interfaces';
import { ItemTypes } from './ItemTypes';

const styles: CSSProperties = {
    border: '1px solid black',
    position: 'relative',
};

type ContainerObject = GameDragObject & {
    top: number;
    left: number;
};

export interface ContainerState {
    boxes: { [key: string]: ContainerObject; };
}

export const Container: FC<GameContainerProps> = ({ hideSourceOnDrag, dragObject, mapObject, coordinates, onChangePosition }) => {
    const [boxes, setBoxes] = useState<{
        [key: string]: ContainerObject;
    }>({
        a: { top: coordinates.y, left: coordinates.x, ...dragObject },
    });

    const additionalStyles: CSSProperties | undefined = mapObject.autoResize
        ? { backgroundSize: "cover", backgroundRepeat: "no-repeat", backgroundPosition: "center center" }
        : undefined;

    const moveBox = useCallback(
        (id: string, left: number, top: number) => {
            setBoxes(
                update(boxes, {
                    [id]: {
                        $merge: { left, top },
                    },
                }),
            );
        },
        [boxes, setBoxes],
    );

    const [, drop] = useDrop(
        () => ({
            accept: ItemTypes.BOX,
            canDrop(item: DragItem, monitor) {
                const delta = monitor.getDifferenceFromInitialOffset() as XYCoord;
                const left = Math.round(item.left + delta.x);
                const top = Math.round(item.top + delta.y);
                return (
                    left >= 0 && left <= (mapObject.width - boxes[item.id].objectWidth)
                    && top >= 0 && top <= (mapObject.height - boxes[item.id].objectHeight)
                );
            },
            drop(item: DragItem, monitor) {
                const delta = monitor.getDifferenceFromInitialOffset() as XYCoord;
                const left = Math.round(item.left + delta.x);
                const top = Math.round(item.top + delta.y);
                moveBox(item.id, left, top);
                onChangePosition({ x: left, y: top });
                return undefined;
            },
        }),
        [moveBox],
    );

    useEffect(() => {
        moveBox("a", coordinates.x, coordinates.y);
    }, [coordinates]);

    useEffect(() => {
        setBoxes({
            a: { top: coordinates.y, left: coordinates.x, ...dragObject },
        });
    }, [dragObject]);

    return drop(
        <div
            style={
                {
                    ...styles,
                    ...mapObject,
                    ...additionalStyles
                }
            }
        >
            <img src={mapObject.image} width="100%" height="100%" style={{ position: "absolute" }} />
            {
                Object.keys(boxes).map((key) => {

                    const { left, top, dragObject } = boxes[key];

                    return (
                        <Box
                            key={key}
                            id={key}
                            left={left}
                            top={top}
                            hideSourceOnDrag={hideSourceOnDrag}
                        >
                            {dragObject}
                        </Box>
                    );
                })
            }
        </div>
    );
};
